/**
 * AIDescriptionField — the "describe what you want, then Improve with AI"
 * pattern that exists across the app (UnifiedProjectWizard,
 * CreateVerificationRequest). Extracted so consumers don't have to inline
 * the same JSX + state machine again.
 *
 * Calls aiService.completeDescription under the hood, then renders the
 * returned suggestions as clickable Paper cards beneath the TextField.
 * Clicking a suggestion replaces the current value with the chosen text
 * (matches the existing UX — see UnifiedProjectWizard.js line ~940 and
 * CreateVerificationRequest.js line ~398).
 *
 * Props:
 *   value              string — current description
 *   onChange(text)     called with the new description
 *   label              field label (rendered above the TextField)
 *   required           appends a red asterisk to the label
 *   placeholder        TextField placeholder
 *   helperText         TextField helperText (rendered below the field)
 *   rows               number of textarea rows (default 4)
 *   projectType        passed to AI (default 'standalone_verification' — the
 *                      same value CreateVerificationRequest uses for non-
 *                      project flows)
 *   taskType           passed to AI as taskType (e.g. package name, milestone
 *                      name) — gives the model context
 *   location           passed to AI for location-aware suggestions; accepts a
 *                      string or a LocationInput object
 *   onError(message)   optional, called when the AI call fails so the caller
 *                      can surface the message in its own Alert / snackbar
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Alert,
} from '@mui/material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import aiService from '../../services/aiService';

const locationToString = (location) => {
  if (!location) return '';
  if (typeof location === 'string') return location;
  return (
    location.address
    || location.formattedAddress
    || location.name
    || (location.city ? `${location.city}` : '')
    || ''
  );
};

const RequiredMark = () => (
  <Box component="span" sx={{ color: 'error.main' }}>*</Box>
);

const AIDescriptionField = ({
  value = '',
  onChange,
  label = 'Description',
  required = false,
  placeholder,
  helperText,
  rows = 4,
  projectType = 'standalone_verification',
  taskType,
  location,
  onError,
}) => {
  const [aiLoading, setAiLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  // Errors render inline below the button — never rely on the caller passing
  // onError, otherwise a failed AI call looks like the button did nothing.
  const [aiError, setAiError] = useState('');

  const reportError = (message) => {
    setAiError(message);
    if (onError) onError(message);
  };

  const handleGenerate = async () => {
    if (!value?.trim()) {
      reportError('Add a rough description first, then AI can improve it.');
      return;
    }
    setAiLoading(true);
    setAiError('');
    try {
      const result = await aiService.completeDescription({
        partialDescription: value,
        projectType,
        taskType: taskType || 'construction verification',
        location: locationToString(location),
      });
      // aiService.completeDescription returns the raw axios .data, which the
      // backend wraps in { success, data: { descriptions } } — match the
      // existing CreateVerificationRequest extraction style.
      const list = result?.data?.descriptions || result?.descriptions || [];
      setSuggestions(list);
      if (list.length === 0) {
        reportError('AI did not return any suggestions. Please try again in a moment.');
      }
    } catch (err) {
      reportError(
        err?.response?.data?.error || err.message || 'Failed to improve description with AI'
      );
    } finally {
      setAiLoading(false);
    }
  };

  const applySuggestion = (suggestion) => {
    if (onChange) onChange(suggestion);
    setSuggestions([]);
  };

  return (
    <Box>
      {label && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {label} {required && <RequiredMark />}
        </Typography>
      )}

      <TextField
        fullWidth
        multiline
        rows={rows}
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        helperText={helperText}
        required={required}
      />

      {/* Always-visible nudge so people know the AI helper exists and use it to
          turn a rough note into a thorough description. */}
      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AutoAwesome />}
          onClick={handleGenerate}
          disabled={aiLoading || !value?.trim()}
          sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2, boxShadow: 2 }}
        >
          {aiLoading ? 'Improving…' : 'Improve with AI'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <AutoAwesome sx={{ fontSize: 15, color: 'primary.main' }} />
          {value?.trim()
            ? 'Tap to expand your notes into a clear, thorough brief.'
            : 'Write a rough note first, then tap “Improve with AI” for a thorough brief.'}
        </Typography>
      </Box>

      {aiError && (
        <Alert severity="warning" onClose={() => setAiError('')} sx={{ mt: 1 }}>
          {aiError}
        </Alert>
      )}

      {suggestions.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: 'block' }}
          >
            AI Suggestions (click to use):
          </Typography>
          {suggestions.map((suggestion, index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                mb: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.50' },
              }}
              onClick={() => applySuggestion(suggestion)}
            >
              <Typography variant="body2">{suggestion}</Typography>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AIDescriptionField;
